import { Logger } from 'student/secure/checkout/sentry'
import api from 'student/shared/util/api'
import { CouponProductsType, CouponType } from 'student/student/types'

const FEDORA_META_ELEMENT_SELECTOR = '#fedora-data'
const PRICING_PLAN_WRAPPER_SELECTOR = '.block__pricing__plan'
const PRICING_PLAN_DESCRIPTION_CONTAINER_SELECTOR =
  '.block__pricing__plan__description__container'
const PRICING_PLAN_DESCRIPTION_SELECTOR = '.block__pricing__plan__description'
const SHOW_DESCRIPTION_BUTTON_SELECTOR =
  '.block__pricing__plan__description__show-button'
const SHOW_DESCRIPTION_BUTTON_TEXT_SELECTOR =
  '.block__pricing__plan__description__show-button__text'
const PRICING_PLAN_PRICE_SELECTOR = '.block__pricing__plan__price'
const PRICING_PLAN_RADIO_SELECTOR = 'input[name="product_id"]'

export const pricingBlock = () => {
  const proto = {
    init(element: HTMLElement) {
      this.element = element
      this.fedoraDataElement = document.querySelector(
        FEDORA_META_ELEMENT_SELECTOR
      )

      this.applyCouponCode = this.applyCouponCode.bind(this)
      this.updatePricingDisplay = this.updatePricingDisplay.bind(this)
      this.toggleDescriptionText = this.toggleDescriptionText.bind(this)
      this.setupDescriptionText = this.setupDescriptionText.bind(this)
      this.parseAndPrependPath = this.parseAndPrependPath.bind(this)
      this.idsToQueryString = this.idsToQueryString.bind(this)
      this.getVATPrices = this.getVATPrices.bind(this)
      this.getVATEndpoint = this.getVATEndpoint.bind(this)
      this.updatePricesWithVAT = this.updatePricesWithVAT.bind(this)

      this.setupDescriptionText()
      this.getVATPrices()
    },

    setupDescriptionText() {
      const pricingPlanDescriptionEls = this.element.querySelectorAll(
        PRICING_PLAN_DESCRIPTION_CONTAINER_SELECTOR
      )

      pricingPlanDescriptionEls.forEach((el: HTMLElement) => {
        const button = el.querySelector(SHOW_DESCRIPTION_BUTTON_SELECTOR)
        const textEl = el.querySelector(PRICING_PLAN_DESCRIPTION_SELECTOR)
        el.dataset.collapsed = 'true'

        if (textEl) {
          textEl.classList.add('hidden')
        }

        if (button) {
          button.addEventListener('click', this.toggleDescriptionText)
        }
      })
    },

    applyCouponCode(data: CouponProductsType) {
      const couponInput = document.createElement('input')
      couponInput.type = 'hidden'
      couponInput.name = 'coupon_code'
      couponInput.value = data.coupon_code

      this.element.insertBefore(couponInput, this.element.firstChild)

      data.discounted_products.forEach(product => {
        const el = this.element.querySelector(
          `[data-id='${product.product_id}']`
        )

        if (el) {
          this.updatePricingDisplay(el, product)
        }
      })

      const allProducts = this.element.querySelectorAll(
        '.block__pricing__plan[data-id]'
      )
      const matchingProductIndices = data.discounted_products
        .map(product => {
          return Array.from(allProducts).findIndex(
            el =>
              (el as HTMLElement).dataset.id === product.product_id.toString()
          )
        })
        // All of the codes we got should match a product, but just in case..
        .filter(x => x >= 0)
      if (matchingProductIndices.length) {
        const firstProduct = allProducts[Math.min(...matchingProductIndices)]
        const radioEl = firstProduct.querySelector(
          PRICING_PLAN_RADIO_SELECTOR
        ) as HTMLInputElement
        if (radioEl) {
          radioEl.checked = true
        }
      }
    },

    updatePricingDisplay(el: HTMLElement, discount: CouponType) {
      const priceEl = el.querySelector(PRICING_PLAN_PRICE_SELECTOR)

      if (priceEl) {
        priceEl.innerHTML = `<span class="block__pricing__plan__price--final">${
          discount.formatted_final_price
        }</span>
        <span class="block__pricing__plan__price--original">${
          discount.original_price
        }</span>`
      }
    },

    toggleDescriptionText(e: MouseEvent) {
      const button = e.currentTarget as HTMLElement

      if (button) {
        const buttonText = button.querySelector(
          SHOW_DESCRIPTION_BUTTON_TEXT_SELECTOR
        )
        const container = button.parentNode as HTMLElement

        if (container) {
          const text = container.querySelector(
            PRICING_PLAN_DESCRIPTION_SELECTOR
          ) as HTMLElement

          if (container.dataset.collapsed === 'true') {
            container.dataset.collapsed = 'false'
            button.classList.add('expanded')

            if (text) {
              text.classList.remove('hidden')
              text.setAttribute('aria-hidden', 'false')
            }

            if (buttonText) {
              buttonText.innerHTML = 'Less'
            }
          } else {
            container.dataset.collapsed = 'true'
            button.classList.remove('expanded')

            if (text) {
              text.classList.add('hidden')
              text.setAttribute('aria-hidden', 'true')
            }

            if (buttonText) {
              buttonText.innerHTML = 'More'
            }
          }
        }
      }
    },

    parseAndPrependPath(courseUrl: string) {
      const path = new URL(courseUrl).pathname
      return `${path}/vat_rates_for_course_products`
    },

    idsToQueryString(pageCourseIds: string) {
      const courseIdArray = JSON.parse(pageCourseIds)

      return courseIdArray.reduce((acc: string, cur: number, index: number) => {
        const prefix = index > 0 ? '&' : ''
        return `${acc}${prefix}course_ids[]=${cur}`
      }, '?')
    },

    getVATEndpoint() {
      let apiEndpoint: string | undefined
      const { pageCourseIds, courseUrl } = this.fedoraDataElement.dataset

      if (courseUrl) {
        apiEndpoint = this.parseAndPrependPath(courseUrl)
      } else if (pageCourseIds) {
        const idString = this.idsToQueryString(pageCourseIds)
        apiEndpoint = `/vat_rates_for_school_default_products${idString}`
      }

      return apiEndpoint
    },

    updatePricesWithVAT(vatAdjustedProductData) {
      vatAdjustedProductData.forEach(data => {
        const pricingEls = document.querySelectorAll(
          `${PRICING_PLAN_WRAPPER_SELECTOR}[data-id="${data.product_id}"]`
        )

        pricingEls.forEach(pricingEl => {
          const priceAmountEl = pricingEl.querySelector(
            PRICING_PLAN_PRICE_SELECTOR
          )

          if (priceAmountEl) {
            priceAmountEl.innerHTML = data.formatted_final_price
          }
        })
      })
    },

    async getVATPrices() {
      if (this.fedoraDataElement) {
        const { schoolPreferences, vatCountry } = this.fedoraDataElement.dataset
        const parsedPreferences =
          schoolPreferences && JSON.parse(schoolPreferences)
        const showVATPRices =
          parsedPreferences &&
          parsedPreferences.display_vat_inclusive_prices.toString() === 'true'
        const currentUrl = window.location.pathname

        // Check in the #fedora-data meta tag to see if the user
        // is from a country that charges VAT
        if (
          showVATPRices &&
          vatCountry &&
          vatCountry !== 'null' &&
          currentUrl.indexOf('/purchased') === -1
        ) {
          const apiEndpoint = this.getVATEndpoint()

          try {
            const vatData = await api(apiEndpoint, { method: 'GET' })
            const vatAdjustedProducts = vatData.vat_adjusted_products

            this.updatePricesWithVAT(vatAdjustedProducts)
          } catch (error) {
            Logger.error(error)
          }
        }
      }
    },
  }

  return Object.create(proto)
}
